// =====================================================
// CHAT CONSTANTS
// Shared, side-effect-free values for the chat module. Centralised so the
// API base URL and other magic values live in exactly one place.
// =====================================================

// Dev vs prod API base. The old rule keyed off `hostname === "localhost"`
// ALONE, so opening the app on 127.0.0.1, a *.local name or a LAN IP silently
// pointed every chat request — REST, voice uploads AND the call-signalling
// socket — at the PRODUCTION backend. Locally developed features then look
// broken for reasons that have nothing to do with their code (a reaction POST
// 404s and the optimistic chip is reverted, calls never signal, uploads land
// in the wrong place). Anything that is not a public host is treated as local
// development, and the backend is addressed on the SAME host the page came
// from so a phone on the LAN reaches the dev server too.
const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "0.0.0.0", "::1", "[::1]"];

const isLocalHostname = (h) =>
  LOCAL_HOSTNAMES.includes(h) ||
  h.endsWith(".local") ||
  /^10\./.test(h) ||
  /^192\.168\./.test(h) ||
  /^172\.(1[6-9]|2\d|3[01])\./.test(h);

export const BASE_URL = isLocalHostname(window.location.hostname)
  ? `http://${window.location.hostname}:5000`
  : "https://success-blog-app-backend.onrender.com";

export const DEFAULT_PIC =
  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

// Quick-reaction palette shown on double-tap / long-press.
export const REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// Disappearing-message timer presets (seconds). 0 = off.
export const DISAPPEARING_OPTIONS = [
  { label: "Off", seconds: 0 },
  { label: "1 hour", seconds: 60 * 60 },
  { label: "1 day", seconds: 24 * 60 * 60 },
  { label: "1 week", seconds: 7 * 24 * 60 * 60 },
];

// Max length for the little quoted preview inside a reply bubble.
export const REPLY_PREVIEW_MAX = 120;

// ── WEBRTC ICE SERVERS ──
// Public STUN works on the same network / simple NATs. Cross-network calls
// behind symmetric NATs REQUIRE a TURN relay. Rather than hardcode secrets,
// TURN credentials are read (optionally) from localStorage so ops can inject
// them per-deployment without a code change:
//   localStorage.setItem("turnUrl", "turn:turn.example.com:3478")
//   localStorage.setItem("turnUsername", "user")
//   localStorage.setItem("turnCredential", "pass")
const buildIceServers = () => {
  const servers = [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ];
  try {
    const turnUrl = localStorage.getItem("turnUrl");
    if (turnUrl) {
      servers.push({
        urls: turnUrl,
        username: localStorage.getItem("turnUsername") || undefined,
        credential: localStorage.getItem("turnCredential") || undefined,
      });
    }
  } catch {
    /* localStorage unavailable — STUN only */
  }
  return servers;
};

export const ICE_SERVERS = buildIceServers();
